/* eslint-disable import/no-cycle */

'use-strict';

import angular, { IHttpService, ILocationService, ITimeoutService } from 'angular';
import moment from 'moment';
import { RootScope } from '../../app.config';
import { AuthAPIService } from '../../shared/api/auth-api.service';
import { SettingsAPIService } from '../../shared/api/settings-api.service';
import { UsersAPIService } from '../../shared/api/users-api.service';
import { NotificationService } from '../../shared/browser-functions/notification.service';
import { BrowserStatisticsService } from '../../shared/browser-functions/statistics.service';
import { ConfigService } from '../../shared/config/config.service';
import { SocketService } from '../../shared/connections/web-socket.service';
import { UsersService } from '../../shared/users/users.service';
import { LoginResult } from '../../src/app/@types/login';
import { UserRole } from '../../src/app/@types/user';
import { ApiService } from '../../src/app/shared/services/api';

export class LoginService {
  private $rootScope: RootScope;

  private socketService: SocketService;

  private notificationService: NotificationService;

  private configService: ConfigService;

  private usersApiService: UsersAPIService;

  private usersService: UsersService;

  private apiService: ApiService;

  private settingsApiService: SettingsAPIService;

  private authApiService: AuthAPIService;

  private $location: ILocationService;

  private $http: IHttpService;

  private $timeout: ITimeoutService;

  private statisticsService: BrowserStatisticsService;

  constructor(
    $rootScope: RootScope,
    socketService: SocketService,
    notificationService: NotificationService,
    configService: ConfigService,
    usersApiService: UsersAPIService,
    usersService: UsersService,
    apiService: ApiService,
    settingsApiService: SettingsAPIService,
    authApiService: AuthAPIService,
    $location: ILocationService,
    $http: IHttpService,
    $timeout: ITimeoutService,
    browserStatisticsService: BrowserStatisticsService,
  ) {
    'ngInject';

    this.$rootScope = $rootScope;
    this.socketService = socketService;
    this.notificationService = notificationService;
    this.configService = configService;
    this.usersApiService = usersApiService;
    this.usersService = usersService;
    this.apiService = apiService;
    this.settingsApiService = settingsApiService;
    this.authApiService = authApiService;
    this.$location = $location;
    this.$http = $http;
    this.$timeout = $timeout;
    this.statisticsService = browserStatisticsService;
  }

  /**
   * Check if current user is already authorized by
   * retrieving previously saved token from local storage
   */
  public isAuthorized = (): boolean => {
    if (this.$rootScope.globals && this.$rootScope.globals.auth) {
      const { authorizedUntil } = this.$rootScope.globals.auth;
      if (!authorizedUntil) return false;

      if (moment().diff(moment(authorizedUntil)) < 0) {
        this.setAuthorization(
          this.$rootScope.globals.auth.authorizedUntil,
          this.$rootScope.globals.auth.token,
        );
        return true;
      }
    }
    return false;
  };

  /**
   * Login server with supplied data at API Server.
   * The refreshToken is automatically saved in a cookie.
   * The userData will be stored in $rootScope.globals
   * The jwt is set for future requests.
   */
  public login = async (
    email: string,
    password: string,
  ): Promise<string> => {
    let afterLoginPath = '/';
    const loginResult = await this.authApiService.loginUser(
      { email, password, tenantId: this.$rootScope.TENANT_ID },
      this.statisticsService.getStatistics(),
    )
      .catch((e) => {
        throw (e);
      });

    const userData = loginResult.userDetails;

    if (loginResult.forcePasswordChange) {
      this.$rootScope.globals.forcePasswordChange = true;
      afterLoginPath = '/password-change';
    }

    this.scheduleRefreshToken(loginResult.authorizedUntil);
    this.configService.setConfig(loginResult.config as { [key: string]: unknown });
    this.$rootScope.$broadcast('updateTierVisibility');
    this.saveUserData(userData);
    await this.setAuthorization(loginResult.authorizedUntil, loginResult.jwt);

    return afterLoginPath;
  };

  /**
   * Call endpoint with refreshToken from Cookie.
   * If successfull the API will return a new token pair of JWT and refreshToken
   * The new refreshtoken is automaticall set, the new jwt must be stored in memory.
   */
  public silentRefresh = async (): Promise<void> => {
    const overwriteToken = (this.$rootScope.globals.overwriteRefreshToken) as string | undefined;
    if (overwriteToken) delete this.$rootScope.globals.overwriteRefreshToken;

    if (this.usersService.currentUserId() === -1) return Promise.reject(new Error('User not yet authroized'));

    const newToken = await this.authApiService.getRefreshToken(
      this.usersService.currentUserId(),
      this.$rootScope.TENANT_ID,
      this.statisticsService.getStatistics(),
      overwriteToken,
    );

    this.scheduleRefreshToken(newToken.authorizedUntil);
    this.configService.setConfig(newToken.config as { [key: string]: unknown });
    this.$rootScope.$broadcast('updateTierVisibility');
    this.saveUserData(newToken.userDetails);
    await this.updateAuthorization(newToken.authorizedUntil, newToken.jwt);
  };

  public logout = (): void => {
    delete this.$rootScope.globals.currentUser;

    if (this.$http.defaults.headers?.common) {
      this.$http.defaults.headers.common.Authorization = 'Basic';
    }
    this.$location.path('/login');
  };

  public getEnabledAPIs = async (): Promise<void> => this.settingsApiService.getEnabledAPIs()
    .then((apis) => {
      this.$rootScope.globals.enabledApis = apis;
    });

  public getAccessibleAPIs = async (): Promise<void> => this.settingsApiService.getAccessibleAPIs()
    .then((apis) => {
      this.$rootScope.globals.accessibleApis = apis;
    });

  /**
   * Call methods, that are needed to be called at app launch, but also need a
   * authenticated user.
   */
  public afterLogin = async (): Promise<void> => {
    const fetchPromises: Promise<void>[] = [];

    fetchPromises.push(this.getEnabledAPIs());
    fetchPromises.push(this.getAccessibleAPIs());
    fetchPromises.push(this.getAllUserData());
    fetchPromises.push(this.getExternalCompanies());

    await Promise.all(fetchPromises);

    this.$rootScope.$broadcast('updatePermissionDirectives');
    this.$rootScope.$broadcast('updateAPIDirectives');
    this.socketService.init();
    this.notificationService.init();

    this.$rootScope.loggedIn = true;
  };

  private getExternalCompanies = (): Promise<void> => this.usersApiService.getExternalCompanies()
    .then((companies) => {
      this.$rootScope.globals.externalCompanies = companies;
    });

  /**
   * Get a list of all users including their names and id's.
   */
  public getAllUserData = async (): Promise<void> => this.usersApiService.getUsers()
    .then((users) => {
      this.$rootScope.globals.userAccounts = users;
    });

  /**
   * Schedule the execution to refresh the authorization token
   */
  public scheduleRefreshToken = (tokenExpiry: Date): void => {
    const expiryDate = moment(tokenExpiry);
    const delay = expiryDate.diff(moment(), 'milliseconds') - 1000;

    if (delay < 0) return;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.$timeout(() => {
      self.silentRefresh();
    }, delay);
  };

  /**
   * Save supplied user data to $rootScope and cookies for later use.
   */
  private saveUserData = (userData: LoginResult['userDetails']): void => {
    this.$rootScope.globals.currentUser = userData;
  };

  /**
   * Set authorization header for future requests to API server.
   * Returns true if user is still authorized.
   * @param {string} token - Authorization token
   */
  private setAuthorization = async (authorizedUntil: Date, jwt: string): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.$http.defaults.headers!.common.Authorization = `Bearer ${jwt}`;
    this.apiService.setAuthorization(jwt);
    this.$rootScope.globals.auth = {
      authorizedUntil,
      token: jwt,
    };
    return this.afterLogin();
  };

  /**
   * Update the authorization header
   * @param {String} token
   */
  private updateAuthorization = (authorizedUntil: Date, jwt: string): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.$http.defaults.headers!.common.Authorization = `Bearer ${jwt}`;
    this.apiService.setAuthorization(jwt);
    this.$rootScope.globals.auth = {
      authorizedUntil,
      token: jwt,
    };
    return Promise.resolve();
  };

  /** When a super admin wants to access the app, a for the server known
   * refresh token must be provided. This token will be injected into the
   * default app launch. */
  public superAdminLogin = (): void => {
    const params = this.$location.search();
    if (!params.token) return;

    const { token } = params;
    this.$rootScope.globals.currentUser = {
      email: 'admin@autoboard.app',
      firstName: 'Autoboard',
      id: -420000,
      isAdmin: true,
      lastName: 'Admin',
      roles: [UserRole.ADMIN],
    };

    this.$rootScope.globals.overwriteRefreshToken = token;
  };
}

export default angular.module('services.login', [])
  .service('loginService', LoginService)
  .name;
